import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import { useLocale } from '$contexts/locale.jsx';
import MobileNavigation from '$partials/global/mobile-navigation.jsx';
import { HeaderLogo, IconArrowRight } from '$partials/icons/index';
import { HeaderLink, HeaderDrawer, LocaleSelector }from './index.js';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const HeaderLandingOpen = ({
    contactSlug,
    menu,
    parent,
    isOpen,
    setIsOpen
}) => {

    const {
        formatURI,
        translate: t,
    } = useLocale();

    // state
    const [stylingWhite, setStylingWhite] = useState((parent === 'Listing' || parent === 'Landing'));

    // set text/logo styling
    useEffect(() => {
        if (isOpen) {
            setStylingWhite(false);
        } else {
            setStylingWhite((parent === 'Listing' || parent === 'Landing'));
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen === false) {
            clearAllBodyScrollLocks();
        }
    }, [isOpen]);

    if (!menu) { return null; }

    // Format links helper function
    const createMenuItem = (item, title, index) => {

        const collection = item?.dropdown?.length > 0 ? item?.dropdown : item?.single;

        const dropdown = collection?.length > 0;

        return {
            collection,
            dropdown,
            title,
            index,
        };
    };

    const menuItems = [
        createMenuItem({ dropdown: menu?.link1DropdownCollection?.items, single: menu?.link1 }, menu?.link1Title, 1),
        createMenuItem({ dropdown: menu?.link2DropdownCollection?.items, single: menu?.link2 }, menu?.link2Title, 2),
        createMenuItem({ dropdown: menu?.link3DropdownCollection?.items, single: menu?.link3 }, menu?.link3Title, 3),
        createMenuItem({ dropdown: menu?.link4DropdownCollection?.items, single: menu?.link4 }, menu?.link4Title, 4),
    ];

    return (
        <>

            <div
                className={`${isOpen ? 'h-auto bg-white fixed w-full z-50 top-0' : 'hidden'}`}
                style={isOpen ? {boxShadow: '0px 4px 99px 99px rgba(0, 0, 0, 0.05)'} : {}}>
                <div className='layout-container'>

                    <header
                        className='pt-30 md:pt-60 w-full flex justify-between type-style-7 font-semibold px-10 sm:px-5 md:px-10 relative items-center'>
                        <div className='flex-none mb-[4px]'>
                            <a href={formatURI('/')}>
                                <HeaderLogo
                                    stylingWhite={stylingWhite}/>
                            </a>
                        </div>
                        <div className='md:hidden flex-grow text-right'>
                            <MobileNavigation
                                menu={menuItems}
                                stylingWhite={stylingWhite} />
                        </div>

                        <>
                            <nav className={`w-full hidden md:block text-center self-center ${isOpen ? 'navigation--is-open' : ' '}`}>
                                <ul className='nav-margin justify-between flex items-center'>
                                    {menuItems.map((item, idx) => (
                                        <HeaderLink
                                            index={item.index}
                                            isOpen={isOpen}
                                            key={idx}
                                            linkItem={item}
                                            setIsOpen={setIsOpen}
                                            stylingWhite={stylingWhite}/>
                                    ))}
                                </ul>
                            </nav>
                        </>
                        <div className={`hidden md:flex flex-row items-center ${stylingWhite ? 'gap-x-30' : 'gap-x-50' }`}>

                            {/* <LocaleSelector stylingWhite={stylingWhite}/> */}
                            <a
                                className={`type-style-7 hidden whitespace-nowrap md:flex flex-row items-center rounded-button py-13 ${stylingWhite ? 'text-white bg-orange px-15' : 'bg-white text-orange pr-15'}`}
                                href={`/contact${contactSlug ? `/${contactSlug}` : ''}`}>
                                {t('Get In Touch')}
                                <IconArrowRight
                                    className='ml-10 h-[13px]'
                                    stroke={`${stylingWhite ? '#FFFFFF' : '#FC5200'}`} />

                            </a>
                        </div>
                    </header>
                </div>
                <>
                    {menuItems.map((link, i) => {
                        if (link?.dropdown) {
                            return (
                                <HeaderDrawer
                                    isOpen={isOpen === i + 1}
                                    key={i}
                                    links={link?.collection}
                                    setIsOpen={setIsOpen}/>
                            );
                        }
                        return null;
                    })}
                </>
            </div>
        </>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

HeaderLandingOpen.propTypes = {
    contactSlug: PropTypes.string,
    menu: PropTypes.shape({
        link1: PropTypes.object,
        link1DropdownCollection: PropTypes.object,
        link1Title: PropTypes.string,
        link2: PropTypes.object,
        link2DropdownCollection: PropTypes.object,
        link2Title: PropTypes.string,
        link3: PropTypes.object,
        link3DropdownCollection: PropTypes.object,
        link3Title: PropTypes.string,
        link4: PropTypes.object,
        link4DropdownCollection: PropTypes.object,
        link4Title: PropTypes.string,
    }),
    parent: PropTypes.string,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func
};

export default HeaderLandingOpen;
